import { NodeModel } from "@minoru/react-dnd-treeview";

import { NavigationElement } from "@/lib/api";
import { isFolderNavigationElement } from "@/lib/navigation-element-kinds";

export function createTreeDataFromNavigationElements(
  navigationElements: NavigationElement[],
) {
  return navigationElements.map<NodeModel<NavigationElement>>((navigationElement) => {
    const isFolder = isFolderNavigationElement(navigationElement);

    return {
      parent: navigationElement.parent_id ?? 0,
      id: navigationElement.id,
      text: navigationElement.title,
      droppable: isFolder,
      data: navigationElement,
    };
  });
}

type ParentChildrenMap = {
  [key: string]: NodeModel<NavigationElement>[];
};

const createParentChildrenData = (treeData: NodeModel<NavigationElement>[]) => {
  const parentsChildrenMap: ParentChildrenMap = {};
  const parents: NodeModel<NavigationElement>[] = [];

  for (const node of treeData) {
    const { parent } = node;

    if (parent === 0) {
      parents.push(node);
    } else {
      if (!parentsChildrenMap[parent]) {
        parentsChildrenMap[parent] = [];
      }

      parentsChildrenMap[parent].push(node);
    }
  }

  return {
    parents,
    parentsChildrenMap,
  };
};

export function createOrderArrayFromTreeData(treeData: NodeModel<NavigationElement>[]) {
  const { parents, parentsChildrenMap } = createParentChildrenData(treeData);

  return parents.map((node) => {
    const children = parentsChildrenMap[node.id] ?? [];

    return {
      id: String(node.id),
      children: children.map((child) => {
        return String(child.id);
      }),
    };
  });
}

/**
 * Puts top-level elements that were filtered out of the tree (hidden warning pages) back
 * into a reorder payload at their original index, so saving the order never drops them.
 */
export function withHiddenElementsInOrder(
  order: ReturnType<typeof createOrderArrayFromTreeData>,
  allElements: NavigationElement[],
): ReturnType<typeof createOrderArrayFromTreeData> {
  const inOrder = new Set(order.map((item) => item.id));
  const result = [...order];
  allElements
    .filter((element) => !element.parent_id)
    .forEach((element, index) => {
      if (inOrder.has(element.id)) return;
      result.splice(Math.min(index, result.length), 0, { id: element.id, children: [] });
    });
  return result;
}
